/**
 * <BottomNav>
 *
 * Mobile-only fixed bottom navigation. Uses `position: fixed` because it's
 * an overlay, not a structural layout element (per the "no absolute
 * positioning for structure" rule).
 *
 * Rules:
 *   • Each tab is ≥ 44px tall AND wide (a real touch target).
 *   • The whole bar respects `env(safe-area-inset-bottom)` so it doesn't
 *     hide under the iOS home indicator.
 *   • Active state uses an animated shared-layout pill (same trick as
 *     SideNav) to indicate the current route with a spring transition.
 *   • The pill has `pointer-events-none` so it never blocks tab taps.
 */

import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@homejournal/utils';
import { NAV_ITEMS } from './nav-items';
import { springInteractive } from '@/components/common/motion-variants';
import { useScopes } from './AuthGate';

export function BottomNav() {
  const { has } = useScopes();
  // At most 4 primary destinations so each tab is wide enough to press.
  const items = NAV_ITEMS.filter((i) => i.primary && (!i.scope || has(i.scope))).slice(0, 4);

  return (
    <nav
      aria-label="Primary mobile"
      className={[
        // Fixed overlay — mobile only.
        'fixed inset-x-0 bottom-0 z-40 md:hidden',
        // Safe-area padding for iOS home-indicator devices.
        'safe-b',
        // Warm blurred background — reads over any content.
        'border-earth-100 border-t bg-white/95 backdrop-blur-xl',
        'dark:bg-earth-950/90 dark:border-white/[0.06]',
      ].join(' ')}
    >
      {/*
        Row of tabs. `items-stretch` so the pill can fill each cell.
        `mx-auto max-w-md` keeps things comfortable on tablets held portrait.
      */}
      <ul className="mx-auto flex max-w-md items-stretch px-2 pt-1.5">
        {items.map(({ to, label, Icon }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              className={({ isActive }) =>
                cn(
                  // Layout + gesture affordance.
                  'group relative flex flex-col items-center justify-center gap-1',
                  // 44×44 minimum hit target.
                  'min-h-11 rounded-2xl px-2 py-2 text-[11px] font-medium',
                  'transition-colors',
                  'focus-visible:ring-brass-400 focus-visible:outline-none focus-visible:ring-2',
                  isActive
                    ? 'text-earth-900 dark:text-earth-50'
                    : 'text-earth-500 dark:text-earth-400',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {/*
                    Active-tab pill. `layoutId` drives the shared-layout
                    animation between tabs. `pointer-events-none` guarantees
                    tap events reach the NavLink underneath.
                  */}
                  {isActive && (
                    <motion.span
                      layoutId="bottomnav-active-pill"
                      aria-hidden="true"
                      className={[
                        'pointer-events-none absolute inset-1 rounded-2xl',
                        'bg-earth-100 dark:bg-white/[0.06]',
                      ].join(' ')}
                      transition={springInteractive}
                    />
                  )}
                  <Icon className="relative z-10 h-5 w-5" />
                  <span className="relative z-10">{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
